/**
 * Global color-theme registry (10 themes) for the AdminClient (cabinet/business) portal.
 *
 * Each theme is applied by setting `data-color-theme="<name>"` on
 * `document.documentElement`, in combination with the existing light/`.dark`
 * class. The actual color values live in `src/index.css` as
 * `[data-color-theme="X"]{...}` (light) and `.dark[data-color-theme="X"]{...}`
 * (dark) blocks. This module only describes the catalog (labels, swatches).
 *
 * Mirrors THEME_SPEC.md — 10 themes, each with a COLORED top bar identity.
 */

export type ThemeMode = 'light' | 'dark' | 'system'

export type ThemeName =
  | 'ocean'
  | 'forest'
  | 'sunset'
  | 'rose'
  | 'midnight'
  | 'navy'
  | 'graphite'
  | 'crimson'
  | 'teal'
  | 'amber'

export interface ThemeMeta {
  name: ThemeName
  /** Human label (FR primary — portal default locale). */
  label: string
  /** Short description for tooltips / settings page. */
  description: string
  /** CSS color used for the swatch dot in the picker (the signature top-bar hue). */
  swatch: string
  /** Accent/primary hue swatch (lighter, for gradient dots). */
  accent: string
}

export const THEME_STORAGE_KEY = 'sgrc-color-theme'

export const DEFAULT_THEME: ThemeName = 'navy'

/**
 * Theme catalog. `swatch`/`accent` are plain CSS `hsl(...)` strings derived from
 * the per-theme top-bar + primary hues in THEME_SPEC.md so the picker dots match
 * the actual applied colors.
 */
export const themes: Record<ThemeName, ThemeMeta> = {
  ocean: {
    name: 'ocean',
    label: 'Océan',
    description: 'Bleu ciel — barre supérieure océan',
    swatch: 'hsl(200 70% 22%)',
    accent: 'hsl(199 89% 48%)',
  },
  forest: {
    name: 'forest',
    label: 'Forêt',
    description: 'Vert émeraude — barre supérieure forêt',
    swatch: 'hsl(155 45% 16%)',
    accent: 'hsl(152 55% 40%)',
  },
  sunset: {
    name: 'sunset',
    label: 'Coucher de soleil',
    description: 'Orange chaud — barre supérieure soleil',
    swatch: 'hsl(18 55% 22%)',
    accent: 'hsl(24 90% 52%)',
  },
  rose: {
    name: 'rose',
    label: 'Rose',
    description: 'Rose/magenta — barre supérieure rose',
    swatch: 'hsl(340 45% 22%)',
    accent: 'hsl(336 78% 56%)',
  },
  midnight: {
    name: 'midnight',
    label: 'Minuit',
    description: 'Indigo/violet — barre supérieure minuit',
    swatch: 'hsl(245 42% 15%)',
    accent: 'hsl(255 70% 60%)',
  },
  navy: {
    name: 'navy',
    label: 'Marine',
    description: 'Bleu marine classique — barre supérieure marine',
    swatch: 'hsl(220 55% 16%)',
    accent: 'hsl(217 72% 46%)',
  },
  graphite: {
    name: 'graphite',
    label: 'Graphite',
    description: 'Gris ardoise — barre supérieure graphite',
    swatch: 'hsl(220 12% 20%)',
    accent: 'hsl(220 9% 46%)',
  },
  crimson: {
    name: 'crimson',
    label: 'Cramoisi',
    description: 'Rouge profond — barre supérieure cramoisi',
    swatch: 'hsl(350 50% 20%)',
    accent: 'hsl(348 75% 48%)',
  },
  teal: {
    name: 'teal',
    label: 'Sarcelle',
    description: 'Sarcelle/cyan — barre supérieure sarcelle',
    swatch: 'hsl(185 55% 16%)',
    accent: 'hsl(182 65% 38%)',
  },
  amber: {
    name: 'amber',
    label: 'Ambre',
    description: 'Ambre/or — barre supérieure ambre',
    swatch: 'hsl(36 50% 20%)',
    accent: 'hsl(38 85% 48%)',
  },
}

/** Ordered list for rendering the swatch picker. */
export const themeList: ThemeMeta[] = [
  themes.navy,
  themes.ocean,
  themes.teal,
  themes.forest,
  themes.midnight,
  themes.rose,
  themes.crimson,
  themes.sunset,
  themes.amber,
  themes.graphite,
]

export const THEME_NAMES: ThemeName[] = themeList.map((t) => t.name)

export function isThemeName(value: unknown): value is ThemeName {
  return typeof value === 'string' && THEME_NAMES.includes(value as ThemeName)
}
